// src/components/atomic-crm/tasks/TaskCreate.tsx
import { Card, CardContent } from "@/components/ds/ui/card";
import { Create } from "@/components/ds/admin/create";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { SelectInput } from "@/components/ds/admin/select-input";
import { TextInput } from "@/components/ds/admin/text-input";
import { FormToolbar } from "../layout/FormToolbar";
import { useMemo, useRef } from "react";
import {
  Form,
  required,
  useGetIdentity,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import { translateChoice } from "@/i18n/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { EntityTypePillSelector } from "./EntityTypePillSelector";
import { EntityAutocomplete } from "./EntityAutocomplete";
import { MultiAssigneeInput } from "./MultiAssigneeInput";
import { transformTaskEntityData } from "./taskEntityUtils";
import { syncActivityAssignees } from "../providers/supabase/syncActivityAssignees";
import { useCurrentSalesId } from "@/hooks/useCurrentSales";

export const TaskCreate = () => {
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const { taskTypes, taskPriorities, taskStatuses } = useConfigurationContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const mySalesId = useCurrentSalesId();
  const pendingAssignees = useRef<number[]>([]);

  const translatedTaskTypes = useMemo(
    () =>
      taskTypes.map((type) => ({
        id: type,
        name: translateChoice(translate, "crm.task.type", type, type),
      })),
    [taskTypes, translate],
  );

  const translatedTaskPriorities = useMemo(
    () =>
      taskPriorities.map((priority) => ({
        ...priority,
        name: translateChoice(
          translate,
          "crm.task.priority",
          priority.id,
          priority.name,
        ),
      })),
    [taskPriorities, translate],
  );

  const translatedTaskStatuses = useMemo(
    () =>
      taskStatuses.map((status) => ({
        ...status,
        name: translateChoice(
          translate,
          "crm.task.status",
          status.id,
          status.name,
        ),
      })),
    [taskStatuses, translate],
  );

  const handleSuccess = async (record: any) => {
    if (record?.id) {
      await syncActivityAssignees(record.id, pendingAssignees.current);
      pendingAssignees.current = [];
    }
    notify(translate("crm.task.notification.created"));
    redirect("list", "crm_activities");
  };

  const defaultValues = useMemo(
    () => ({
      due_date: new Date().toISOString(),
      type: taskTypes[0] || "Call",
      priority: "medium",
      status: "todo",
      entity_type: "none",
      assignee_ids: mySalesId != null ? [mySalesId] : [],
    }),
    [mySalesId, taskTypes],
  );

  if (isIdentityPending) return null;

  return (
    <Create
      resource="crm_activities"
      redirect={false}
      mutationOptions={{ onSuccess: handleSuccess }}
      transform={(data) => {
        // Capture assignee_ids before the transform strips them.
        pendingAssignees.current = Array.isArray(data.assignee_ids)
          ? data.assignee_ids.map(Number)
          : [];

        const entity = transformTaskEntityData(data);
        return {
          type: "task",
          text: data.text,
          date: data.due_date || new Date().toISOString(),
          due_date: data.due_date ?? null,
          done_date: null,
          priority: data.priority ?? "medium",
          status: data.status ?? "todo",
          contact_id: entity.contact_id ?? null,
          company_id: entity.company_id ?? null,
          deal_id: entity.deal_id ?? null,
          sales_id: mySalesId ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }}
    >
      <div className="mt-4 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <Form defaultValues={defaultValues}>
              <TextInput
                autoFocus
                source="text"
                label={translate("crm.task.field.description")}
                validate={required()}
                multiline
                className="w-full"
              />

              <div className="grid grid-cols-2 gap-4 mt-4">
                <EntityTypePillSelector />
              </div>

              <div className="mt-4">
                <EntityAutocomplete />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <DateTimeInput source="due_date" validate={required()} />
                <SelectInput
                  source="type"
                  validate={required()}
                  choices={translatedTaskTypes}
                  label={translate("crm.task.field.type")}
                />
                <SelectInput
                  source="priority"
                  choices={translatedTaskPriorities}
                  label={translate("crm.task.field.priority")}
                />
                <SelectInput
                  source="status"
                  choices={translatedTaskStatuses}
                  label={translate("crm.task.field.status")}
                />
              </div>

              <div className="mt-4">
                <MultiAssigneeInput
                  source="assignee_ids"
                  helperText="People who need to act on this."
                />
              </div>

              <FormToolbar />
            </Form>
          </CardContent>
        </Card>
      </div>
    </Create>
  );
};